<template>
  <div v-if="user" class="mt-5">
    <div class="mt-3">
      <Label required>{{ $t("common.first_name") }}</Label>
      <InputField
        :disabled="userRole === 'student'"
        :placeholder="
          $t('common.first_name') + ' ' + $t('common.max_35_characters')
        "
        max-length="35"
        v-model="state.form.firstName"
        class="d-block"
        test-id="profile-settings-first-name"
        :invalid-feedback="errors.firstName || state.serverErrors.firstName"
      ></InputField>
    </div>
    <div class="mt-3">
      <Label required>{{ $t("common.last_name") }}</Label>
      <InputField
        :disabled="userRole === 'student'"
        :placeholder="
          $t('common.last_name') + ' ' + $t('common.max_35_characters')
        "
        :max-length="35"
        v-model="state.form.lastName"
        class="d-block"
        test-id="profile-settings-last-name"
        :invalid-feedback="errors.lastName || state.serverErrors.lastName"
      ></InputField>
    </div>
    <div v-if="userRole === 'student'" class="mt-3">
      <Label required>{{ $t("common.email") }}</Label>
      <InputField
        :disabled="userRole === 'student'"
        :placeholder="$t('common.email')"
        v-model="state.email"
        class="d-block"
        test-id="profile-settings-email"
      ></InputField>
    </div>
    <div v-if="isVisibleAllowAppRequest && userRole !== 'student'" class="mt-4">
      <div class="d-flex justify-content-between py-2">
        <Label>{{ $t("common.appointment_requests_to_you") }}</Label>
        <CustomSwitch
          type="radio"
          v-model="state.form.allowAppRequest"
        ></CustomSwitch>
      </div>
      <div class="mt-2">
        <InfoBox class="px-3 py-3">
          {{
            $t(
              "common.when_the_toggle_is_blue_students_will_be_able_to_create_appointment_requests_to_you"
            )
          }}
        </InfoBox>
      </div>
      <div class="d-flex align-items-center justify-content-between mt-4">
        <Label class="mb-0">{{
          $t("common.notifications_for_appointment_requests")
        }}</Label>
        <div class="d-flex flex-column gap-4">
          <div class="d-flex justify-content-between align-items-center">
            <Label>{{ $t("common.email_notification") }}</Label>
            <CustomSwitch
              type="radio"
              class="ms-2"
              v-model="state.form.appointment_notifications.email"
            ></CustomSwitch>
          </div>
          <div class="d-flex justify-content-between align-items-center">
            <Label>{{ $t("common.bell_notification") }}</Label>
            <CustomSwitch
              type="radio"
              class="ms-2"
              v-model="state.form.appointment_notifications.browser"
              @change="handleBrowserNotificationChange"
            ></CustomSwitch>
          </div>
        </div>
      </div>
    </div>
    <div v-if="userRole !== 'student'" class="mt-4">
      <div class="d-flex justify-content-between py-2">
        <Label>{{ $t("common.passes_to_from_you") }}</Label>
        <CustomSwitch
          type="radio"
          v-model="state.form.allowCreatePasses"
        ></CustomSwitch>
      </div>
      <InfoBox class="px-3 py-3">
        {{
          $t(
            "common.when_the_toggle_is_blue_adults_and_students_will_be_able_to_create_passes_to_from_you"
          )
        }}
      </InfoBox>
    </div>
    <div
      v-if="
        activeSchool.is_premium &&
        isActiveAppoinmentModule &&
        userRole !== 'student'
      "
      class="mt-4"
    >
      <div class="d-flex justify-content-between py-2">
        <div class="me-3">
          <Label>{{ $t("common.appointment_pass_email_reminders") }}</Label>
          <InfoBox class="px-3 py-3">
            {{
              $t(
                "common.select_which_appointment_pass_reminder_emails_to_be_sent_to_you"
              )
            }}
          </InfoBox>
        </div>
        <div>
          <div class="d-flex justify-content-between py-2">
            <Label>{{ $t("common.20_minute") }}</Label>
            <CustomSwitch
              type="radio"
              class="ms-2"
              v-model="state.form.apt_reminder_emails['20min']"
            ></CustomSwitch>
          </div>
          <div class="d-flex justify-content-between py-2">
            <Label>{{ $t("common.5_minute") }}</Label>
            <CustomSwitch
              type="radio"
              class="ms-2"
              v-model="state.form.apt_reminder_emails['5min']"
            ></CustomSwitch>
          </div>
        </div>
      </div>
    </div>
    <div v-if="userRole !== 'student'" class="mt-4">
      <Label>{{ $t("common.pin") }}</Label>
      <InputField
        placeholder="Enter the PIN"
        min-length="4"
        max-length="6"
        v-model="state.form.pin"
        class="d-block"
        :invalid-feedback="errors.teacher_pin || state.serverErrors.teacher_pin"
      ></InputField>
      <InfoBox class="mt-2 px-3 py-3">
        {{
          $t(
            "common.your_pin_needs_to_be_between_4_and_6_letters_numbers_or_characters_long_any_combination_of_these_but_no_spaces"
          )
        }}
      </InfoBox>
    </div>
    <div
      v-if="
        userRole === 'student' &&
        isActiveKioskModule &&
        !activeSchool.hide_student_kiosk_passcode_field
      "
      class="mt-5"
    >
      <Label>{{ $t("common.kiosk_passcode") }}</Label>
      <InputField
        placeholder="Enter passcode"
        v-model="state.kioskPasscode"
        input-type="text"
        :append-icon="state.kioskPasscodeIcon"
        @click-append-icon="onClickAppendIcon"
        :style="state.kioskInputTypeStyle"
        class="d-block"
        :invalid-feedback="
          errors.kioskPasscode || state.serverErrors.kioskPasscode
        "
      ></InputField>
    </div>
    <div>
      <InfoBox
        v-if="state.serverRes"
        class="my-4"
        :class="{ danger: Object.keys(state.serverErrors).length }"
        :title="state.serverRes.title"
        >{{ state.serverRes.message }}</InfoBox
      >
    </div>
    <div class="my-4">
      <LoadingButton
        v-if="
          userRole !== 'student' ||
          (isActiveKioskModule &&
            !activeSchool.hide_student_kiosk_passcode_field)
        "
        :is-loading="state.isProcessing"
        @click="
          userRole === 'student' && isActiveKioskModule
            ? updateKioskPasscode()
            : submit()
        "
        class="my-2 px-4"
        solid
        rounded
        >{{ $t("common.save_changes") }}</LoadingButton
      >
    </div>
  </div>
</template>

<script>
import { disableBrowserPushNotifications } from "@/helpers/featureFlags"
import { reactive, computed, onMounted } from "vue"
import { useStore } from "vuex"
import useVuelidate from "@vuelidate/core"
import { helpers, required } from "@vuelidate/validators"
import Label from "@/v3components/shared/Form/Label.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import CustomSwitch from "@/v3components/shared/Form/CustomSwitch.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import visitorUtils from "@/helpers/visitorUtils"
import useFcmToken from "@/composables/useFcmToken"

export default {
  name: "ProfileSettingForm",
  components: {
    Label,
    InputField,
    CustomSwitch,
    InfoBox,
    LoadingButton
  },
  props: {
    user: {
      type: Object
    },
    avatar: {
      type: String,
      default: ""
    },
    userRole: {
      type: String,
      default: ""
    },
    activeAppoinmentModuleOptions: {
      type: Object
    },
    isActiveAppoinmentModule: {
      type: Boolean,
      default: false
    },
    isActiveKioskModule: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const store = useStore()
    const { initializeFcmToken } = useFcmToken()

    const user = computed(() => {
      return store.getters["authentication/user"]
    })

    const state = reactive({
      form: {
        firstName: "",
        lastName: "",
        newPass: "",
        allowAppRequest: true,
        allowCreatePasses: true,
        apt_reminder_emails: {
          "20min": false,
          "5min": false
        },
        appointment_notifications: {
          email: false,
          browser: false
        },
        pin: ""
      },
      email: props.user.email,
      kioskPasscode: props.user.kiosk_passcode,
      kioskInputTypeStyle: "text-security:disc; -webkit-text-security:disc;",
      kioskPasscodeIcon: "ri-eye-off-fill",
      isProcessing: false,
      serverErrors: {},
      serverRes: null
    })

    onMounted(() => {
      const profileData = {
        firstName: props.user.first_name,
        lastName: props.user.last_name,
        allowAppRequest: !!props.user.allow_appointment_requests,
        allowCreatePasses: !!props.user.allow_passes_to_me,
        pin: props.user.pin,
        apt_reminder_emails: {
          "20min": !!props.user.twenty_min_reminder,
          "5min": !!props.user.five_min_reminder
        },
        appointment_notifications: {
          email: !!props.user.appointment_email_notification,
          browser: !!props.user.appointment_browser_notification
        }
      }
      state.form = Object.assign(state.form, profileData)
    })

    const validationMessages = {
      required: "This field is required"
    }
    const validations = {
      form: {
        firstName: {
          required: helpers.withMessage(validationMessages.required, required)
        },
        lastName: {
          required: helpers.withMessage(validationMessages.required, required)
        }
      }
    }

    const v$ = useVuelidate(validations.form, state.form)

    const isFormValid = computed(() => !v$.value.$invalid)

    const errors = computed(() => {
      const errorObj = {}
      v$.value.$errors.forEach((err) => {
        errorObj[err.$property] = err.$message
      })

      return errorObj
    })

    const isVisibleAllowAppRequest = computed(() => {
      return (
        props.isActiveAppoinmentModule &&
        props.activeAppoinmentModuleOptions &&
        props.activeAppoinmentModuleOptions[props.userRole]
      )
    })

    const activeSchool = computed(() => store.getters["schools/activeSchool"])

    const onClickAppendIcon = () => {
      state.kioskInputTypeStyle =
        state.kioskInputTypeStyle ===
        "text-security:disc; -webkit-text-security:disc;"
          ? ""
          : "text-security:disc; -webkit-text-security:disc;"
      state.kioskPasscodeIcon =
        state.kioskPasscodeIcon === "ri-eye-off-fill"
          ? "ri-eye-fill"
          : "ri-eye-off-fill"
    }

    const submit = () => {
      if (isFormValid.value) {
        state.isProcessing = true
        let data = {
          first_name: state.form.firstName,
          last_name: state.form.lastName,
          allow_appointment_requests: state.form.allowAppRequest,
          allow_passes_to_me: state.form.allowCreatePasses,
          is_searchable: props.user.is_searchable,
          twenty_min_reminder: state.form.apt_reminder_emails["20min"],
          five_min_reminder: state.form.apt_reminder_emails["5min"],
          appointment_email_notification:
            state.form.appointment_notifications.email,
          appointment_browser_notification:
            state.form.appointment_notifications.browser
        }
        if (state.form.newPass) {
          data.password = state.form.newPass
        }
        if (state.form.pin) {
          data.teacher_pin = state.form.pin
          data.pin = state.form.pin
        }
        if (props.avatar) {
          data.avatar = props.avatar === "delete" ? null : props.avatar
          if (data.avatar) {
            const formData = new FormData()
            for (const key in data) {
              if (Object.hasOwnProperty.call(data, key)) {
                const element = data[key]
                formData.append(key, element)
              }
            }
            data = formData
          }
        }
        updateProfile(data)
      } else {
        v$.value.$touch()
      }
    }

    const updateProfile = (data) => {
      store
        .dispatch("authentication/updateUserProfile", data)
        .then(() => {
          setResponseInfoBox("Success", "Successfully saved!")
          state.isProcessing = false
          state.serverErrors = {}
          store.dispatch("authentication/getUserDetails", {
            clearCacheEntry: true
          })
          store.dispatch("authentication/getUserSettings", {
            clearCacheEntry: true
          })
          setTimeout(() => {
            setResponseInfoBox()
          }, 1800)
        })
        .catch((err) => {
          const res = err.response.data
          state.serverErrors = res.errors ? res.errors : {}
          setResponseInfoBox("Error", res.message)
          state.isProcessing = false
        })
    }

    const updateKioskPasscode = () => {
      store
        .dispatch("authentication/updateKioskPass", state.kioskPasscode)
        .then(() => {
          setResponseInfoBox("Success", "Successfully saved!")
          state.isProcessing = false
          state.serverErrors = {}
          store.dispatch("authentication/getUserDetails", {
            clearCacheEntry: true
          })
          store.dispatch("authentication/getUserSettings", {
            clearCacheEntry: true
          })
          setTimeout(() => {
            setResponseInfoBox()
            state.kioskPasscode = null
          }, 1800)
        })
        .catch((err) => {
          const res = err.response.data
          state.serverErrors = res.errors ? res.errors : {}
          setResponseInfoBox("Error", res.message)
          state.isProcessing = false
        })
    }

    const setResponseInfoBox = (title, message) => {
      if (title || message) {
        state.serverRes = {
          message,
          title
        }
      } else {
        state.serverRes = null
      }
    }

    const handleBrowserNotificationChange = (value) => {
      if (disableBrowserPushNotifications) return

      if (value) {
        visitorUtils.forSettings.enableBrowserNotification(true)
        if (user?.value?.id) {
          initializeFcmToken(user.value.id)
        }
      }
    }

    return {
      state,
      v$,
      isFormValid,
      errors,
      isVisibleAllowAppRequest,
      activeSchool,
      onClickAppendIcon,
      submit,
      updateKioskPasscode,
      handleBrowserNotificationChange
    }
  }
}
</script>
